/*
  Native "frequently bought together" (see sections/cyan-bought-together.liquid).
  Recomputes the running total as companion checkboxes toggle. There is no separate
  add-to-cart button -- clicking the page's own Add to Cart button (the ProductForm
  custom element in assets/theme.js) is intercepted here in the capture phase, before
  ProductForm's own bubble-phase submit handler runs, so we can fold the checked
  companions into the SAME /cart/add.js request instead of a second click.

  Event dispatching after a successful add deliberately mirrors ProductForm._onSubmit's
  own pattern exactly (document.documentElement, "cart:updated" / "cart:refresh" with a
  {cart: ...} detail) so the existing cart badge/drawer refresh identically to a normal
  single-item add -- see assets/theme.js's ProductForm class.
*/
(function () {
  class CyanBoughtTogether extends HTMLElement {
    connectedCallback() {
      this.checkboxes = Array.from(this.querySelectorAll('.cyan-bought-together__checkbox'));
      this.totalEl = this.querySelector('[data-total-value]');
      this.mainPrice = this.querySelector('.cyan-bought-together__item--main .cyan-bought-together__price');
      this.basePrice = this.parsePrice(this.mainPrice ? this.mainPrice.textContent : '0');

      this.checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => this.updateTotal());
      });
      this.updateTotal();

      this.setupPreviewDialog();

      // Scoped to the id naming convention set by sections/main-product.liquid
      // ('product-form-main-' + product.id + '-' + section.id) -- a generic
      // form[action*="/cart/add"] selector also matches quick-buy popover forms
      // for other products lower on the page, and even non-product forms (e.g.
      // installment/BNPL calculator widgets) that happen to share the same action.
      this.mainForm = document.querySelector('form[id^="product-form-main-"]');
      if (this.mainForm) {
        // Capture phase on document fires before the form's own bubble-phase
        // ProductForm._onSubmit listener, so stopping propagation here pre-empts it.
        this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
        document.addEventListener('submit', this.onDocumentSubmit, true);
      }
    }

    disconnectedCallback() {
      if (this.onDocumentSubmit) {
        document.removeEventListener('submit', this.onDocumentSubmit, true);
      }
    }

    parsePrice(text) {
      const digits = (text || '').replace(/[^0-9.]/g, '');
      return parseFloat(digits) || 0;
    }

    updateTotal() {
      let total = this.basePrice;
      this.checkboxes.forEach((checkbox) => {
        if (checkbox.checked) {
          total += parseFloat(checkbox.dataset.price) || 0;
        }
      });
      if (this.totalEl) {
        this.totalEl.innerHTML = this.formatMoney(total);
      }
    }

    formatMoney(amount) {
      // Reuses the currency icon already rendered on the main price (see
      // assets/cyan-currency-icon.js, which swaps the store's text prefix for
      // an icon site-wide) rather than guessing a locale format -- clone
      // whatever prefix markup is actually there and append the number.
      const iconEl = this.mainPrice ? this.mainPrice.querySelector('.cyan-aed-icon') : null;
      const prefix = iconEl ? iconEl.outerHTML : '';
      return prefix + amount.toFixed(2);
    }

    setupPreviewDialog() {
      this.dialog = this.querySelector('[data-preview-dialog]');
      if (!this.dialog) return;

      this.previewImageEl = this.dialog.querySelector('[data-preview-image-el]');
      this.previewTitleEl = this.dialog.querySelector('[data-preview-title-el]');
      this.previewPriceEl = this.dialog.querySelector('[data-preview-price-el]');
      this.previewLinkEl = this.dialog.querySelector('[data-preview-link-el]');

      this.querySelectorAll('.cyan-bought-together__preview-trigger').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.previewImageEl.src = trigger.dataset.previewImage;
          this.previewImageEl.alt = trigger.dataset.previewTitle;
          this.previewTitleEl.textContent = trigger.dataset.previewTitle;
          // innerHTML, not textContent -- {{ price | money }} renders as
          // "<span class=money>...</span>", not plain text.
          this.previewPriceEl.innerHTML = trigger.dataset.previewPrice;
          this.previewLinkEl.href = trigger.dataset.previewUrl;
          this.dialog.showModal();
        });
      });

      this.dialog.querySelector('[data-preview-close]').addEventListener('click', () => this.dialog.close());
      // Native <dialog> already closes on a backdrop click only if we detect it's
      // outside the content box, since the backdrop itself is part of the element.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    onDocumentSubmit(event) {
      if (event.target !== this.mainForm) return;
      event.preventDefault();
      event.stopPropagation();
      this.submitCombined(event.target);
    }

    async submitCombined(form) {
      const idInput = form.querySelector('input[name="id"]:not([disabled])');
      // The quantity-selector block's <input> is associated to the form via the
      // HTML5 form="..." attribute, not DOM nesting (it renders as a sibling
      // block, not inside <form>), so form.querySelector() never finds it --
      // query by that attribute instead. form.getAttribute('id') is required
      // here too: form.id resolves to the <input name="id"> control instead of
      // the form's own id attribute, the same named-item shadowing quirk noted
      // elsewhere in this file.
      const formId = form.getAttribute('id');
      const quantityInput = document.querySelector('input[name="quantity"][form="' + formId + '"]') || form.querySelector('input[name="quantity"]');
      const mainVariantId = idInput ? idInput.value : this.getAttribute('data-fallback-variant-id');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      const items = [{ id: mainVariantId, quantity }];
      this.checkboxes.forEach((checkbox) => {
        if (checkbox.checked) {
          items.push({ id: checkbox.dataset.variantId, quantity: 1 });
        }
      });

      const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
      submitButtons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      });

      try {
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['mini-cart'] }),
        });
        const responseJson = await response.json();

        submitButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });

        if (!response.ok) {
          form.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        form.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { variant: responseJson.hasOwnProperty('items') ? responseJson['items'][0] : responseJson },
        }));

        const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        const cartContent = await cartResponse.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent },
        }));
        cartContent.sections = responseJson.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables.settings.cartType === 'drawer' && form.closest('.drawer') === null,
          },
        }));
      } catch (e) {
        submitButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
        form.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }
  }

  if (!customElements.get('cyan-bought-together')) {
    customElements.define('cyan-bought-together', CyanBoughtTogether);
  }
})();
